import { useState, useEffect } from 'react'
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Activity,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Download,
  Calendar,
  Target,
  Zap,
  UserMinus,
  Heart,
  FileText,
  Star,
  Info,
  HelpCircle
} from 'lucide-react'

// Tooltip component for metric explanations
function MetricTooltip({ text }: { text: string }) {
  return (
    <div className="group relative inline-flex ml-1">
      <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
      <div className="fixed-tooltip absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 w-48 text-left shadow-lg pointer-events-none"
           style={{ zIndex: 99999 }}>
        {text}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  )
}
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RePieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts'
import { Select } from '../components/ui/Select'
import { PageHeader } from '../components/ui/PageHeader'
import {
  partnerAdminApi,
  type AnalyticsOverview,
  type FinancialAnalytics,
  type GrowthAnalytics,
  type ChurnAnalytics,
  type LTVAnalytics,
  type ClientPerformance
} from '../services/api'

type TabType = 'overview' | 'financial' | 'growth' | 'churn' | 'ltv' | 'clients'

export function Analytics() {
  const [activeTab, setActiveTab] = useState<TabType>('overview')
  const [period, setPeriod] = useState<'7d' | '30d' | '90d' | '12m'>('30d')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Data states
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null)
  const [financial, setFinancial] = useState<FinancialAnalytics | null>(null)
  const [growth, setGrowth] = useState<GrowthAnalytics | null>(null)
  const [churn, setChurn] = useState<ChurnAnalytics | null>(null)
  const [ltv, setLtv] = useState<LTVAnalytics | null>(null)
  const [clientPerformance, setClientPerformance] = useState<ClientPerformance | null>(null)

  // Load data based on active tab
  useEffect(() => {
    loadData()
  }, [activeTab, period])

  const loadData = async () => {
    setLoading(true)
    setError(null)

    try {
      switch (activeTab) {
        case 'overview':
          const overviewData = await partnerAdminApi.analytics.getOverview(period)
          setOverview(overviewData)
          break
        case 'financial':
          const financialData = await partnerAdminApi.analytics.getFinancial()
          setFinancial(financialData)
          break
        case 'growth':
          const growthPeriod = period === '7d' || period === '30d' ? '6m' : period === '90d' ? '12m' : '12m'
          const growthData = await partnerAdminApi.analytics.getGrowth(growthPeriod as any)
          setGrowth(growthData)
          break
        case 'churn':
          const churnData = await partnerAdminApi.analytics.getChurn(period)
          setChurn(churnData)
          break
        case 'ltv':
          const ltvData = await partnerAdminApi.analytics.getLTV()
          setLtv(ltvData)
          break
        case 'clients':
          const clientData = await partnerAdminApi.analytics.getClientPerformance({ period })
          setClientPerformance(clientData)
          break
      }
    } catch (err) {
      console.error('Error loading analytics:', err)
      setError('Erro ao carregar dados. Usando dados de demonstração.')
      // Load fallback data
      loadFallbackData()
    } finally {
      setLoading(false)
    }
  }

  const loadFallbackData = () => {
    switch (activeTab) {
      case 'overview':
        setOverview(generateFallbackOverview())
        break
      case 'financial':
        setFinancial(generateFallbackFinancial())
        break
      case 'growth':
        setGrowth(generateFallbackGrowth())
        break
      case 'churn':
        setChurn(generateFallbackChurn())
        break
      case 'ltv':
        setLtv(generateFallbackLTV())
        break
      case 'clients':
        setClientPerformance(generateFallbackClientPerformance())
        break
    }
  }

  // Fallback data generators
  const generateFallbackOverview = (): AnalyticsOverview => ({
    period: '30d',
    dateRange: { start: '2025-11-03', end: '2025-12-03' },
    summary: {
      revenue: { total: 125000, growth: 12.5, avgPerClient: 5208 },
      clients: { total: 24, active: 22, churnRate: 2.1 },
      operations: { healthScore: 92, uptime: 99.8, documentsProcessed: 1250 },
      support: { ticketsCreated: 45, ticketsResolved: 42, satisfactionScore: 4.6 }
    },
    highlights: [
      { type: 'positive', icon: 'trending-up', title: 'Crescimento Forte', description: 'Receita cresceu 12.5% no período' },
      { type: 'positive', icon: 'check-circle', title: 'Sistema Saudável', description: 'Score de saúde: 92%' },
      { type: 'positive', icon: 'star', title: 'Alta Satisfação', description: 'Nota de satisfação: 4.6/5' }
    ],
    generatedAt: new Date().toISOString()
  })

  const generateFallbackFinancial = (): FinancialAnalytics => ({
    reportId: 'financial-001',
    type: 'financial',
    period: { startDate: '2025-09-01', endDate: '2025-12-03', duration: '3 meses' },
    partner: { id: 'partner-1', totalClients: 24, activeClients: 22 },
    summary: {
      totalRevenue: 375000,
      totalExpenses: 112500,
      netProfit: 262500,
      profitMargin: '70.00',
      averageRevenuePerClient: 15625,
      growthRate: '15.2'
    },
    breakdown: {
      byClient: [
        { id: '1', name: 'Clínica São Lucas', revenue: 45000, expenses: 13500, profit: 31500, profitMargin: '70.0', transactionCount: 150 },
        { id: '2', name: 'Dr. Carlos Medicina', revenue: 38000, expenses: 11400, profit: 26600, profitMargin: '70.0', transactionCount: 120 },
        { id: '3', name: 'Centro Médico Saúde', revenue: 52000, expenses: 15600, profit: 36400, profitMargin: '70.0', transactionCount: 180 }
      ],
      byMonth: [
        { month: '2025-09', revenue: 115000, expenses: 34500, clientCount: 22 },
        { month: '2025-10', revenue: 125000, expenses: 37500, clientCount: 23 },
        { month: '2025-11', revenue: 135000, expenses: 40500, clientCount: 24 }
      ],
      byService: [
        { service: 'Contabilidade Básica', revenue: 150000, clientsUsing: 24, avgTicket: 6250 },
        { service: 'Gestão Fiscal', revenue: 120000, clientsUsing: 20, avgTicket: 6000 },
        { service: 'Consultoria Tributária', revenue: 75000, clientsUsing: 15, avgTicket: 5000 },
        { service: 'Folha de Pagamento', revenue: 30000, clientsUsing: 10, avgTicket: 3000 }
      ],
      expenses: [
        { category: 'Pessoal', amount: 56250, percentage: 50 },
        { category: 'Tecnologia', amount: 22500, percentage: 20 },
        { category: 'Marketing', amount: 11250, percentage: 10 },
        { category: 'Operacional', amount: 11250, percentage: 10 },
        { category: 'Administrativo', amount: 11250, percentage: 10 }
      ]
    },
    trends: {
      revenueChart: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - (29 - i) * 86400000).toISOString().slice(0, 10),
        revenue: 4000 + Math.random() * 2000,
        expenses: 1200 + Math.random() * 600
      })),
      clientGrowth: [
        { month: '2025-07', totalClients: 18, newClients: 3, churnedClients: 1 },
        { month: '2025-08', totalClients: 20, newClients: 3, churnedClients: 1 },
        { month: '2025-09', totalClients: 22, newClients: 3, churnedClients: 1 },
        { month: '2025-10', totalClients: 23, newClients: 2, churnedClients: 1 },
        { month: '2025-11', totalClients: 24, newClients: 2, churnedClients: 1 }
      ],
      profitabilityTrend: [
        { month: '2025-07', revenue: 95000, expenses: 28500, profitMargin: '70.0' },
        { month: '2025-08', revenue: 105000, expenses: 31500, profitMargin: '70.0' },
        { month: '2025-09', revenue: 115000, expenses: 34500, profitMargin: '70.0' },
        { month: '2025-10', revenue: 125000, expenses: 37500, profitMargin: '70.0' },
        { month: '2025-11', revenue: 135000, expenses: 40500, profitMargin: '70.0' }
      ]
    },
    generatedAt: new Date().toISOString()
  })

  const generateFallbackGrowth = (): GrowthAnalytics => ({
    period: '12m',
    dateRange: { start: '2024-12-01', end: '2025-12-01' },
    metrics: { revenueGrowth: 45.2, clientGrowthRate: 33.3, profitGrowthRate: 48.5 },
    trends: {
      clientGrowth: [
        { month: '2025-01', totalClients: 14, newClients: 2, churnedClients: 0 },
        { month: '2025-03', totalClients: 16, newClients: 3, churnedClients: 1 },
        { month: '2025-05', totalClients: 18, newClients: 3, churnedClients: 1 },
        { month: '2025-07', totalClients: 20, newClients: 3, churnedClients: 1 },
        { month: '2025-09', totalClients: 22, newClients: 3, churnedClients: 1 },
        { month: '2025-11', totalClients: 24, newClients: 3, churnedClients: 1 }
      ],
      revenueGrowth: [
        { month: '2025-01', revenue: 75000, expenses: 22500, profitMargin: '70.0' },
        { month: '2025-03', revenue: 85000, expenses: 25500, profitMargin: '70.0' },
        { month: '2025-05', revenue: 95000, expenses: 28500, profitMargin: '70.0' },
        { month: '2025-07', revenue: 105000, expenses: 31500, profitMargin: '70.0' },
        { month: '2025-09', revenue: 115000, expenses: 34500, profitMargin: '70.0' },
        { month: '2025-11', revenue: 125000, expenses: 37500, profitMargin: '70.0' }
      ],
      profitability: [
        { month: '2025-01', revenue: 75000, expenses: 22500, profitMargin: '70.0' },
        { month: '2025-03', revenue: 85000, expenses: 25500, profitMargin: '70.0' },
        { month: '2025-05', revenue: 95000, expenses: 28500, profitMargin: '70.0' },
        { month: '2025-07', revenue: 105000, expenses: 31500, profitMargin: '70.0' },
        { month: '2025-09', revenue: 115000, expenses: 34500, profitMargin: '70.0' },
        { month: '2025-11', revenue: 125000, expenses: 37500, profitMargin: '70.0' }
      ]
    },
    projections: {
      next3Months: 140000,
      next6Months: 155000,
      next12Months: 180000,
      confidence: 'medium',
      assumptions: ['Baseado na taxa de crescimento atual', 'Sem alterações no mercado', 'Mantendo base de clientes']
    },
    insights: ['Receita por cliente está crescendo', 'Novos clientes contribuindo para crescimento', 'Crescimento acelerado - considere expandir']
  })

  const generateFallbackChurn = (): ChurnAnalytics => ({
    period: '90d',
    metrics: { churnRate: '2.1', atRiskCount: 3, retentionRate: '97.9', avgLifetime: 24 },
    atRiskClients: [
      { id: '1', name: 'Consultório Beta', healthScore: 45, lastActivity: '2025-11-15', riskFactors: ['Baixo engajamento', 'Inatividade'], recommendedActions: ['Agendar reunião', 'Oferecer treinamento'] },
      { id: '2', name: 'Dr. Silva Clínica', healthScore: 55, lastActivity: '2025-11-20', riskFactors: ['Health score baixo'], recommendedActions: ['Verificar satisfação'] },
      { id: '3', name: 'Centro Diagnóstico', healthScore: 62, lastActivity: '2025-11-25', riskFactors: ['Poucos logins'], recommendedActions: ['Demonstrar novos recursos'] }
    ],
    churnReasons: [
      { reason: 'Preço', percentage: 35 },
      { reason: 'Mudança de Contador', percentage: 25 },
      { reason: 'Fechamento', percentage: 20 },
      { reason: 'Insatisfação', percentage: 12 },
      { reason: 'Outros', percentage: 8 }
    ],
    retentionTrend: [
      { month: '2025-09', retentionRate: '97.5', churnedClients: 1, newClients: 3 },
      { month: '2025-10', retentionRate: '98.0', churnedClients: 0, newClients: 2 },
      { month: '2025-11', retentionRate: '97.9', churnedClients: 1, newClients: 2 }
    ],
    generatedAt: new Date().toISOString()
  })

  const generateFallbackLTV = (): LTVAnalytics => ({
    metrics: { ltv: 125000, arpu: 5208, avgLifetimeMonths: 24, cac: 18750, ltvCacRatio: '6.67' },
    segmentation: {
      byPlan: [
        { plan: 'Básico', ltv: 87500, arpu: 3646, clientCount: 8 },
        { plan: 'Profissional', ltv: 162500, arpu: 6771, clientCount: 16 }
      ],
      byTenure: [
        { tenure: '0-6 meses', ltv: 62500, avgMonths: 3 },
        { tenure: '6-12 meses', ltv: 100000, avgMonths: 9 },
        { tenure: '12+ meses', ltv: 187500, avgMonths: 24 }
      ]
    },
    trends: [
      { month: '2025-07', ltv: 115000, arpu: 4792 },
      { month: '2025-08', ltv: 118000, arpu: 4917 },
      { month: '2025-09', ltv: 120000, arpu: 5000 },
      { month: '2025-10', ltv: 122500, arpu: 5104 },
      { month: '2025-11', ltv: 125000, arpu: 5208 }
    ],
    generatedAt: new Date().toISOString()
  })

  const generateFallbackClientPerformance = (): ClientPerformance => ({
    period: '30d',
    totalClients: 24,
    clients: [
      { id: '1', name: 'Clínica São Lucas', status: 'active', lastActivity: new Date().toISOString(), healthScore: 95, monthlyUsage: { logins: 45, documentsUploaded: 28, reportsGenerated: 12, hoursActive: 35 } },
      { id: '2', name: 'Dr. Carlos Medicina', status: 'active', lastActivity: new Date().toISOString(), healthScore: 88, monthlyUsage: { logins: 38, documentsUploaded: 22, reportsGenerated: 8, hoursActive: 28 } },
      { id: '3', name: 'Centro Médico Saúde', status: 'active', lastActivity: new Date().toISOString(), healthScore: 92, monthlyUsage: { logins: 52, documentsUploaded: 35, reportsGenerated: 15, hoursActive: 42 } },
      { id: '4', name: 'Consultório Beta', status: 'active', lastActivity: '2025-11-15T10:00:00Z', healthScore: 45, monthlyUsage: { logins: 8, documentsUploaded: 5, reportsGenerated: 2, hoursActive: 6 } },
      { id: '5', name: 'Dr. Silva Clínica', status: 'active', lastActivity: '2025-11-20T14:30:00Z', healthScore: 55, monthlyUsage: { logins: 12, documentsUploaded: 8, reportsGenerated: 3, hoursActive: 10 } }
    ],
    summary: { avgHealthScore: 75, activeClients: 22, clientsNeedingAttention: 3 },
    generatedAt: new Date().toISOString()
  })

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }

  const formatPercent = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value
    return `${num >= 0 ? '+' : ''}${num.toFixed(1)}%`
  }

  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444']

  const tabs = [
    { id: 'overview', label: 'Visão Geral', icon: BarChart3 },
    // Pós-MVP: Financeiro - não há dados de despesas do partner
    // { id: 'financial', label: 'Financeiro', icon: DollarSign },
    // Pós-MVP: Crescimento - não há histórico de receita/lucro para calcular
    // { id: 'growth', label: 'Crescimento', icon: TrendingUp },
    { id: 'churn', label: 'Churn', icon: UserMinus },
    { id: 'ltv', label: 'LTV', icon: Heart },
    { id: 'clients', label: 'Clientes', icon: Users }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Analytics"
        subtitle="Métricas e insights do seu negócio"
        actions={
          <>
            <Select
              value={period}
              onChange={(e) => setPeriod(e.target.value as any)}
              options={[
                { value: '7d', label: 'Últimos 7 dias' },
                { value: '30d', label: 'Últimos 30 dias' },
                { value: '90d', label: 'Últimos 90 dias' },
                { value: '12m', label: 'Último ano' }
              ]}
            />
            <button
              onClick={loadData}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </>
        }
      />

      {/* Error Banner */}
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
          <span className="text-yellow-800">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-yellow-600 hover:text-yellow-800">×</button>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </div>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-green-600 animate-spin" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && overview && (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Receita Total</p>
                          <p className="text-2xl font-bold text-gray-900">{formatCurrency(overview.summary.revenue.total)}</p>
                          <p className={`text-sm mt-1 flex items-center gap-1 ${overview.summary.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {overview.summary.revenue.growth >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                            {formatPercent(overview.summary.revenue.growth)}
                          </p>
                        </div>
                        <DollarSign className="w-8 h-8 text-green-600" />
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Clientes</p>
                          <p className="text-2xl font-bold text-gray-900">{overview.summary.clients.total}</p>
                          <p className="text-sm text-gray-500 mt-1">{overview.summary.clients.active} ativos</p>
                        </div>
                        <Users className="w-8 h-8 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Métricas Secundárias</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-sm text-gray-500 mb-1 flex items-center justify-center">
                          Receita por Cliente
                          <MetricTooltip text="Receita média mensal gerada por cada cliente" />
                        </p>
                        <p className="text-xl font-bold text-gray-900">{formatCurrency(overview.summary.revenue.avgPerClient)}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-sm text-gray-500 mb-1 flex items-center justify-center">
                          Taxa de Churn
                          <MetricTooltip text="Percentual de clientes que cancelaram no período" />
                        </p>
                        <p className="text-xl font-bold text-gray-900">{overview.summary.clients.churnRate}%</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-sm text-gray-500 mb-1">Documentos Processados</p>
                        <p className="text-xl font-bold text-gray-900">{overview.summary.operations.documentsProcessed.toLocaleString()}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4 text-center">
                        <p className="text-sm text-gray-500 mb-1">Tickets Criados</p>
                        <p className="text-xl font-bold text-gray-900">{overview.summary.support.ticketsCreated}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Financial Tab */}
              {activeTab === 'financial' && financial && (
                <div className="space-y-6">
                  {/* Financial Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500">Receita Total</p>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(financial.summary.totalRevenue)}</p>
                      <p className="text-xs text-gray-400 mt-1">Crescimento: {formatPercent(parseFloat(financial.summary.growthRate))}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500">Despesas</p>
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(financial.summary.totalExpenses)}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500">Lucro Líquido</p>
                      <p className="text-2xl font-bold text-blue-600">{formatCurrency(financial.summary.netProfit)}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500">Margem de Lucro</p>
                      <p className="text-2xl font-bold text-purple-600">{financial.summary.profitMargin}%</p>
                    </div>
                  </div>

                  {/* Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Receita vs Despesas</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={financial.trends.profitabilityTrend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" stroke="#9ca3af" />
                            <YAxis stroke="#9ca3af" />
                            <Tooltip formatter={(value: number) => formatCurrency(value)} />
                            <Legend />
                            <Area type="monotone" dataKey="revenue" stroke="#10b981" fill="#10b981" fillOpacity={0.3} name="Receita" />
                            <Area type="monotone" dataKey="expenses" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} name="Despesas" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Receita por Serviço</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <RePieChart>
                            <Pie
                              data={financial.breakdown.byService}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={(entry) => entry.service.split(' ')[0]}
                              outerRadius={80}
                              dataKey="revenue"
                            >
                              {financial.breakdown.byService.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value: number) => formatCurrency(value)} />
                          </RePieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Top Clients */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-4">Top Clientes por Receita</h3>
                    <div className="space-y-3">
                      {financial.breakdown.byClient.slice(0, 5).map((client, index) => (
                        <div key={client.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                          <div className="flex items-center gap-3">
                            <span className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center font-semibold">
                              {index + 1}
                            </span>
                            <span className="font-medium text-gray-900">{client.name}</span>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">{formatCurrency(client.revenue)}</p>
                            <p className="text-xs text-gray-500">{client.transactionCount} transações</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Growth Tab */}
              {activeTab === 'growth' && growth && (
                <div className="space-y-6">
                  {/* Growth Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Crescimento de Receita</p>
                          <p className="text-2xl font-bold text-green-600">{formatPercent(growth.metrics.revenueGrowth)}</p>
                        </div>
                        <TrendingUp className="w-8 h-8 text-green-600" />
                      </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Crescimento de Clientes</p>
                          <p className="text-2xl font-bold text-blue-600">{formatPercent(growth.metrics.clientGrowthRate)}</p>
                        </div>
                        <Users className="w-8 h-8 text-blue-600" />
                      </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Crescimento de Lucro</p>
                          <p className="text-2xl font-bold text-purple-600">{formatPercent(growth.metrics.profitGrowthRate)}</p>
                        </div>
                        <Target className="w-8 h-8 text-purple-600" />
                      </div>
                    </div>
                  </div>

                  {/* Growth Charts */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Crescimento de Clientes</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={growth.trends.clientGrowth}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" stroke="#9ca3af" />
                            <YAxis stroke="#9ca3af" />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="newClients" fill="#10b981" name="Novos" />
                            <Bar dataKey="churnedClients" fill="#ef4444" name="Churn" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Evolução da Receita</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={growth.trends.revenueGrowth}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" stroke="#9ca3af" />
                            <YAxis stroke="#9ca3af" />
                            <Tooltip formatter={(value: number) => formatCurrency(value)} />
                            <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} name="Receita" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Projections */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-4 flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Projeções
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <p className="text-sm text-gray-500">Próximos 3 meses</p>
                        <p className="text-xl font-bold text-blue-600">{formatCurrency(growth.projections.next3Months)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <p className="text-sm text-gray-500">Próximos 6 meses</p>
                        <p className="text-xl font-bold text-blue-600">{formatCurrency(growth.projections.next6Months)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-blue-100">
                        <p className="text-sm text-gray-500">Próximos 12 meses</p>
                        <p className="text-xl font-bold text-blue-600">{formatCurrency(growth.projections.next12Months)}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-blue-700">
                      <Info className="w-4 h-4 mt-0.5" />
                      <div>
                        <p className="font-medium">Confiança: {growth.projections.confidence}</p>
                        <ul className="list-disc list-inside mt-1">
                          {growth.projections.assumptions.map((assumption, i) => (
                            <li key={i}>{assumption}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Insights */}
                  <div className="space-y-2">
                    {growth.insights.map((insight, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-gray-700">{insight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Churn Tab */}
              {activeTab === 'churn' && churn && (
                <div className="space-y-6">
                  {/* Churn Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500 flex items-center">
                        Taxa de Churn
                        <MetricTooltip text="Percentual de clientes que cancelaram no período" />
                      </p>
                      <p className="text-2xl font-bold text-red-600">{churn.metrics.churnRate}%</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500 flex items-center">
                        Clientes em Risco
                        <MetricTooltip text="Clientes com sinais de possível cancelamento" />
                      </p>
                      <p className="text-2xl font-bold text-yellow-600">{churn.metrics.atRiskCount}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500 flex items-center">
                        Taxa de Retenção
                        <MetricTooltip text="Percentual de clientes que permaneceram ativos" />
                      </p>
                      <p className="text-2xl font-bold text-green-600">{churn.metrics.retentionRate}%</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500 flex items-center">
                        Tempo Médio de Vida
                        <MetricTooltip text="Média de meses que os clientes permanecem ativos" />
                      </p>
                      <p className="text-2xl font-bold text-blue-600">{churn.metrics.avgLifetime} meses</p>
                    </div>
                  </div>

                  {/* At Risk Clients */}
                  {churn.atRiskClients.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h3 className="font-semibold text-red-900 mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Clientes em Risco ({churn.atRiskClients.length})
                      </h3>
                      <div className="space-y-3">
                        {churn.atRiskClients.map((client) => (
                          <div key={client.id} className="bg-white rounded-lg p-4 border border-red-100">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-gray-900">{client.name}</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                client.healthScore < 50 ? 'bg-red-100 text-red-800' :
                                client.healthScore < 70 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                Health: {client.healthScore}%
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 mb-2">
                              Última atividade: {new Date(client.lastActivity).toLocaleDateString('pt-BR')}
                            </div>
                            <div className="flex flex-wrap gap-2 mb-2">
                              {client.riskFactors.map((factor, i) => (
                                <span key={i} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                                  {factor}
                                </span>
                              ))}
                            </div>
                            <div className="text-sm text-gray-500">
                              <strong>Ações recomendadas:</strong> {client.recommendedActions.join(', ')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Churn Reasons */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Motivos de Churn</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <RePieChart>
                            <Pie
                              data={churn.churnReasons}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={(entry) => `${entry.reason}: ${entry.percentage}%`}
                              outerRadius={80}
                              dataKey="percentage"
                            >
                              {churn.churnReasons.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </RePieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">Tendência de Retenção</h3>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={churn.retentionTrend}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" stroke="#9ca3af" />
                            <YAxis stroke="#9ca3af" domain={[90, 100]} />
                            <Tooltip />
                            <Line type="monotone" dataKey="retentionRate" stroke="#10b981" strokeWidth={2} name="Retenção %" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* LTV Tab */}
              {activeTab === 'ltv' && ltv && (
                <div className="space-y-6">
                  {/* LTV Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600 flex items-center">
                            LTV
                            <MetricTooltip text="Lifetime Value: valor total que um cliente gera durante toda sua vida" />
                          </p>
                          <p className="text-2xl font-bold text-purple-600">{formatCurrency(ltv.metrics.ltv)}</p>
                        </div>
                        <Heart className="w-8 h-8 text-purple-600" />
                      </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <p className="text-sm font-medium text-gray-600 flex items-center">
                        ARPU
                        <MetricTooltip text="Average Revenue Per User: receita média mensal por cliente" />
                      </p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(ltv.metrics.arpu)}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <p className="text-sm font-medium text-gray-600 flex items-center">
                        Tempo de Vida Médio
                        <MetricTooltip text="Média de meses que os clientes permanecem ativos" />
                      </p>
                      <p className="text-2xl font-bold text-gray-900">{ltv.metrics.avgLifetimeMonths} meses</p>
                    </div>
                    {/* Pós-MVP: CAC e LTV/CAC requerem dados de despesas com marketing/vendas */}
                    {/* <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <p className="text-sm font-medium text-gray-600">CAC</p>
                      <p className="text-2xl font-bold text-gray-900">{formatCurrency(ltv.metrics.cac)}</p>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-gray-200">
                      <p className="text-sm font-medium text-gray-600">LTV/CAC</p>
                      <p className="text-2xl font-bold text-green-600">{ltv.metrics.ltvCacRatio}x</p>
                    </div> */}
                  </div>

                  {/* Segmentation */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">LTV por Plano</h3>
                      <div className="space-y-3">
                        {ltv.segmentation.byPlan.map((segment) => (
                          <div key={segment.plan} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                            <div>
                              <p className="font-medium text-gray-900">{segment.plan}</p>
                              <p className="text-sm text-gray-500">{segment.clientCount} clientes</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">{formatCurrency(segment.ltv)}</p>
                              <p className="text-xs text-gray-500">ARPU: {formatCurrency(segment.arpu)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4">LTV por Tempo de Vida</h3>
                      <div className="space-y-3">
                        {ltv.segmentation.byTenure.map((segment) => (
                          <div key={segment.tenure} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                            <div>
                              <p className="font-medium text-gray-900">{segment.tenure}</p>
                              <p className="text-sm text-gray-500">Média: {segment.avgMonths} meses</p>
                            </div>
                            <p className="font-semibold text-gray-900">{formatCurrency(segment.ltv)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* LTV Trend - Removido: não há dados históricos reais disponíveis */}
                </div>
              )}

              {/* Clients Tab */}
              {activeTab === 'clients' && clientPerformance && (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500 flex items-center">
                        Health Score Médio
                        <MetricTooltip text="Média da saúde dos clientes baseado em uso e engajamento" />
                      </p>
                      <p className="text-2xl font-bold text-gray-900">{clientPerformance.summary.avgHealthScore.toFixed(1)}%</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500">Clientes Ativos</p>
                      <p className="text-2xl font-bold text-green-600">{clientPerformance.summary.activeClients}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500 flex items-center">
                        Precisando Atenção
                        <MetricTooltip text="Clientes com health score baixo ou inatividade prolongada" />
                      </p>
                      <p className="text-2xl font-bold text-yellow-600">{clientPerformance.summary.clientsNeedingAttention}</p>
                    </div>
                  </div>

                  {/* Client List */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Cliente</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700 flex items-center">
                          Health Score
                          <MetricTooltip text="Índice de saúde do cliente baseado em logins, documentos e atividade" />
                        </th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Logins</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Documentos</th>
                          <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Última Atividade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientPerformance.clients.map((client) => (
                          <tr key={client.id} className="border-t border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium text-gray-900">{client.name}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                client.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {client.status === 'active' ? 'Ativo' : 'Inativo'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      client.healthScore >= 80 ? 'bg-green-500' :
                                      client.healthScore >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${client.healthScore}%` }}
                                  />
                                </div>
                                <span className="text-sm text-gray-600">{client.healthScore}%</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-gray-600">{client.monthlyUsage.logins}</td>
                            <td className="py-3 px-4 text-gray-600">{client.monthlyUsage.documentsUploaded}</td>
                            <td className="py-3 px-4 text-gray-600">
                              {new Date(client.lastActivity).toLocaleDateString('pt-BR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
